import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { PageHeader } from "../components/common/PageHeader";
import { reportAPI, schoolsAPI, campusAPI } from "../api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { AlertTriangle, Eye, Ban, ChevronLeft, ChevronRight, Filter, TrendingUp, FileWarning, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { ScrollArea } from "../components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import type { Report, BanUserRequest, UpdateReportStatusRequest, BulkUpdateReportsRequest } from "../types/report";

export function ReportsPage() {
  const [selectedSchool, setSelectedSchool] = useState<string>("");
  const [selectedCampus, setSelectedCampus] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedType, setSelectedType] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReports, setSelectedReports] = useState<Set<string>>(new Set());
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [banDuration, setBanDuration] = useState<string>("permanent");
  const [banReason, setBanReason] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [bulkAction, setBulkAction] = useState<"resolve" | "dismiss" | "under_review">("resolve");
  const [bulkAdminNotes, setBulkAdminNotes] = useState("");
  const queryClient = useQueryClient();

  const pageLimit = 20;

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedSchool, selectedCampus, selectedStatus, selectedType]);

  // Reset campus when school changes
  useEffect(() => {
    setSelectedCampus("");
  }, [selectedSchool]);

  // Dialog cleanup handlers
  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedReport(null);
    setAdminNotes("");
  };

  const handleCloseBanDialog = () => {
    setBanDialogOpen(false);
    setSelectedReport(null);
    setBanReason("");
    setBanDuration("permanent");
  };

  const handleCloseBulkDialog = () => {
    setBulkActionDialogOpen(false);
    setBulkAdminNotes("");
    setBulkAction("resolve");
  };

  // Fetch schools
  const { data: schoolsData } = useQuery({
    queryKey: ['schools'],
    queryFn: () => schoolsAPI.getAllSchools(),
  });

  // Fetch campuses (filtered by school if selected)
  const { data: campusesData } = useQuery({
    queryKey: ['campuses', selectedSchool],
    queryFn: () => campusAPI.getAllCampuses(),
    enabled: !!selectedSchool,
  });

  // Fetch reports with filters
  const { data: reportsData, isLoading: reportsLoading, error: reportsError } = useQuery({
    queryKey: ['reports', selectedSchool, selectedCampus, selectedStatus, selectedType, currentPage],
    queryFn: () => reportAPI.getReports({
      school_id: selectedSchool || undefined,
      campus_id: selectedCampus || undefined,
      status: (selectedStatus || undefined) as 'pending' | 'under_review' | 'resolved' | 'dismissed' | undefined,
      report_type: (selectedType || undefined) as 'user' | 'message' | 'event' | 'idea' | undefined,
      page: currentPage,
      limit: pageLimit,
    }),
  });

  // Fetch report details
  const { data: reportDetailsData, isLoading: detailsLoading } = useQuery({
    queryKey: ['reportDetails', selectedReport?._id],
    queryFn: () => reportAPI.getReportDetails(selectedReport!._id),
    enabled: !!selectedReport && detailsDialogOpen,
  });

  // Update report status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ reportId, data }: { reportId: string; data: UpdateReportStatusRequest }) =>
      reportAPI.updateReportStatus(reportId, data),
    onSuccess: () => {
      toast.success("Report status updated successfully");
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      handleCloseDetailsDialog();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Failed to update report status");
    },
  });

  // Ban user mutation
  const banUserMutation = useMutation({
    mutationFn: ({ reportId, data }: { reportId: string; data: BanUserRequest }) =>
      reportAPI.banUserFromReport(reportId, data),
    onSuccess: () => {
      toast.success("User banned successfully");
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      queryClient.invalidateQueries({ queryKey: ['reportDetails'] });
      handleCloseBanDialog();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Failed to ban user");
    },
  });

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: (data: BulkUpdateReportsRequest) => reportAPI.bulkUpdateReports(data),
    onSuccess: () => {
      toast.success("Reports updated successfully");
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      setSelectedReports(new Set());
      handleCloseBulkDialog();
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || "Failed to update reports");
    },
  });

  const schools = schoolsData || [];
  const campuses = campusesData || [];
  const reports = reportsData?.reports || [];
  const pagination = reportsData?.pagination || { page: 1, limit: pageLimit, total: 0, pages: 0 };

  // Calculate statistics across all reports (would ideally come from backend)
  const totalReports = pagination.total;
  const pendingReports = reports.filter(r => r.status === 'pending').length;
  const resolvedReports = reports.filter(r => r.status === 'resolved').length;
  const underReviewReports = reports.filter(r => r.status === 'under_review').length;

  const handleViewDetails = (report: Report) => {
    setSelectedReport(report);
    setDetailsDialogOpen(true);
  };

  const handleBanUser = (report: Report) => {
    setSelectedReport(report);
    setBanDialogOpen(true);
  };

  const handleUpdateStatus = (status: 'pending' | 'under_review' | 'resolved' | 'dismissed') => {
    if (!selectedReport) return;
    updateStatusMutation.mutate({
      reportId: selectedReport._id,
      data: { status, admin_notes: adminNotes },
    });
  };

  const handleConfirmBan = () => {
    if (!selectedReport) return;

    // Parse ban duration with validation
    let parsedDuration: number | undefined = undefined;
    if (banDuration !== 'permanent') {
      const parsed = parseInt(banDuration, 10);
      if (isNaN(parsed) || parsed <= 0) {
        toast.error("Invalid ban duration");
        return;
      }
      parsedDuration = parsed;
    }

    const data: BanUserRequest = {
      ban_duration: parsedDuration,
      ban_reason: banReason || undefined,
      resolve_related_reports: true,
    };
    banUserMutation.mutate({
      reportId: selectedReport._id,
      data,
    });
  };

  const handleBulkAction = () => {
    if (selectedReports.size === 0) return;
    bulkUpdateMutation.mutate({
      report_ids: Array.from(selectedReports),
      action: bulkAction,
      admin_notes: bulkAdminNotes || undefined,
    });
  };

  const toggleReportSelection = (reportId: string) => {
    const newSelected = new Set(selectedReports);
    if (newSelected.has(reportId)) {
      newSelected.delete(reportId);
    } else {
      newSelected.add(reportId);
    }
    setSelectedReports(newSelected);
  };

  const toggleAllReports = () => {
    if (selectedReports.size === reports.length) {
      setSelectedReports(new Set());
    } else {
      setSelectedReports(new Set(reports.map(r => r._id)));
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending': return 'destructive';
      case 'under_review': return 'default';
      case 'resolved': return 'secondary';
      case 'dismissed': return 'outline';
      default: return 'default';
    }
  };

  const getReasonLabel = (reason: string) => {
    const labels: Record<string, string> = {
      'harassment_threats': 'Harassment/Threats',
      'inappropriate_content': 'Inappropriate Content',
      'spam_fake': 'Spam/Fake',
      'underage_policy': 'Underage/Policy',
      'made_uncomfortable': 'Made Uncomfortable',
      'violence_dangerous': 'Violence/Dangerous',
      'intellectual_property': 'Intellectual Property',
      'other': 'Other',
    };
    return labels[reason] || reason;
  };

  const capitalizeFirst = (text: string) => {
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ');
  };

  if (reportsLoading) {
    return (
      <DashboardLayout>
        <LoadingState message="Loading reports..." />
      </DashboardLayout>
    );
  }

  if (reportsError) {
    return (
      <DashboardLayout>
        <ErrorState message="Failed to load reports" />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PageHeader
          title="Content Moderation"
          description="Manage user reports and content violations across all campuses"
        />

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Reports</CardTitle>
              <FileWarning className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalReports}</div>
              <p className="text-xs text-muted-foreground">Across all campuses</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <AlertTriangle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pendingReports}</div>
              <p className="text-xs text-muted-foreground">On current page</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Under Review</CardTitle>
              <TrendingUp className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{underReviewReports}</div>
              <p className="text-xs text-muted-foreground">On current page</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Resolved</CardTitle>
              <ShieldAlert className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{resolvedReports}</div>
              <p className="text-xs text-muted-foreground">On current page</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
            <CardDescription>Filter reports by university, campus, status, and type</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>University/School</Label>
                <Select value={selectedSchool || "all"} onValueChange={(v) => setSelectedSchool(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All universities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All universities</SelectItem>
                    {schools.map((school: { _id: string; name: string }) => (
                      <SelectItem key={school._id} value={school._id}>
                        {school.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Campus</Label>
                <Select
                  value={selectedCampus || "all"}
                  onValueChange={(v) => setSelectedCampus(v === "all" ? "" : v)}
                  disabled={!selectedSchool}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All campuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All campuses</SelectItem>
                    {campuses.map((campus: { _id: string; campus_name: string }) => (
                      <SelectItem key={campus._id} value={campus._id}>
                        {campus.campus_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={selectedStatus || "all"} onValueChange={(v) => setSelectedStatus(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="under_review">Under Review</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Report Type</Label>
                <Select value={selectedType || "all"} onValueChange={(v) => setSelectedType(v === "all" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="message">Message</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="idea">Idea</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions */}
        {selectedReports.size > 0 && (
          <Card className="border-primary">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={selectedReports.size === reports.length}
                    onCheckedChange={toggleAllReports}
                  />
                  <span className="text-sm font-medium">
                    {selectedReports.size} report{selectedReports.size !== 1 ? 's' : ''} selected
                  </span>
                </div>
                <Button onClick={() => setBulkActionDialogOpen(true)}>
                  Bulk Actions
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reports Table */}
        <Card>
          <CardHeader>
            <CardTitle>Reports</CardTitle>
            <CardDescription>
              Showing {reports.length} of {totalReports} reports
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedReports.size === reports.length && reports.length > 0}
                        onCheckedChange={toggleAllReports}
                      />
                    </TableHead>
                    <TableHead>Reported By</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>School</TableHead>
                    <TableHead>Campus</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                        No reports found
                      </TableCell>
                    </TableRow>
                  ) : (
                    reports.map((report) => (
                      <TableRow key={report._id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedReports.has(report._id)}
                            onCheckedChange={() => toggleReportSelection(report._id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {report.reported_by.first_name} {report.reported_by.last_name}
                            </div>
                            <div className="text-xs text-muted-foreground">{report.reported_by.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{capitalizeFirst(report.report_type)}</Badge>
                        </TableCell>
                        <TableCell>{getReasonLabel(report.reason)}</TableCell>
                        <TableCell>{report.school_id.name}</TableCell>
                        <TableCell>{report.campus_id?.campus_name || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(report.status)}>
                            {formatStatus(report.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(report.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                Actions
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleViewDetails(report)}>
                                <Eye className="mr-2 h-4 w-4" />
                                View Details
                              </DropdownMenuItem>
                              {report.report_type === 'user' && (
                                <DropdownMenuItem
                                  onClick={() => handleBanUser(report)}
                                  className="text-destructive"
                                >
                                  <Ban className="mr-2 h-4 w-4" />
                                  Ban User
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.pages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.min(pagination.pages, p + 1))}
                    disabled={currentPage === pagination.pages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Report Details Dialog */}
        <Dialog open={detailsDialogOpen} onOpenChange={(open) => !open && handleCloseDetailsDialog()}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Report Details</DialogTitle>
              <DialogDescription>
                Review the report details and take appropriate action
              </DialogDescription>
            </DialogHeader>
            {detailsLoading ? (
              <div className="py-8">
                <LoadingState message="Loading report details..." />
              </div>
            ) : reportDetailsData ? (
              <ScrollArea className="max-h-[60vh]">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Reported By</Label>
                      <p className="font-medium">
                        {reportDetailsData.reported_by.first_name} {reportDetailsData.reported_by.last_name}
                      </p>
                      <p className="text-sm text-muted-foreground">{reportDetailsData.reported_by.email}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Report Type</Label>
                      <p className="font-medium">{capitalizeFirst(reportDetailsData.report_type)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Reason</Label>
                      <p className="font-medium">{getReasonLabel(reportDetailsData.reason)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Status</Label>
                      <Badge variant={getStatusBadgeVariant(reportDetailsData.status)}>
                        {formatStatus(reportDetailsData.status)}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">School</Label>
                      <p className="font-medium">{reportDetailsData.school_id.name}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Campus</Label>
                      <p className="font-medium">{reportDetailsData.campus_id?.campus_name || '-'}</p>
                    </div>
                  </div>

                  <div>
                    <Label className="text-muted-foreground">Description</Label>
                    <p className="mt-1">{reportDetailsData.description}</p>
                  </div>

                  {reportDetailsData.admin_notes && (
                    <div>
                      <Label className="text-muted-foreground">Admin Notes</Label>
                      <p className="mt-1">{reportDetailsData.admin_notes}</p>
                    </div>
                  )}

                  {reportDetailsData.relatedReports && reportDetailsData.relatedReports.length > 0 && (
                    <div>
                      <Label className="text-muted-foreground">Related Reports</Label>
                      <p className="text-sm mt-1">
                        {reportDetailsData.totalRelatedReports} total related report(s)
                      </p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Admin Notes</Label>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Add notes about this report..."
                      maxLength={1000}
                      rows={3}
                    />
                    <p className="text-xs text-muted-foreground">{adminNotes.length}/1000 characters</p>
                  </div>
                </div>
              </ScrollArea>
            ) : null}
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={handleCloseDetailsDialog}>
                Close
              </Button>
              <Button
                variant="outline"
                onClick={() => handleUpdateStatus('dismissed')}
                disabled={updateStatusMutation.isPending}
              >
                Dismiss
              </Button>
              <Button
                variant="secondary"
                onClick={() => handleUpdateStatus('under_review')}
                disabled={updateStatusMutation.isPending}
              >
                Mark Under Review
              </Button>
              <Button
                onClick={() => handleUpdateStatus('resolved')}
                disabled={updateStatusMutation.isPending}
              >
                Resolve
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ban User Dialog */}
        <Dialog open={banDialogOpen} onOpenChange={(open) => !open && handleCloseBanDialog()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ban User</DialogTitle>
              <DialogDescription>
                This will ban the reported user and optionally resolve related reports
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Ban Duration</Label>
                <Select value={banDuration} onValueChange={setBanDuration}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="permanent">Permanent</SelectItem>
                    <SelectItem value="1">1 day</SelectItem>
                    <SelectItem value="3">3 days</SelectItem>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Ban Reason</Label>
                <Textarea
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  placeholder="Explain why this user is being banned..."
                  maxLength={500}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">{banReason.length}/500 characters</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseBanDialog}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmBan}
                disabled={banUserMutation.isPending}
              >
                {banUserMutation.isPending ? "Banning..." : "Ban User"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Action Dialog */}
        <Dialog open={bulkActionDialogOpen} onOpenChange={(open) => !open && handleCloseBulkDialog()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Bulk Update Reports</DialogTitle>
              <DialogDescription>
                Apply an action to {selectedReports.size} selected report(s)
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Action</Label>
                <Select value={bulkAction} onValueChange={(v) => setBulkAction(v as 'resolve' | 'dismiss' | 'under_review')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resolve">Resolve</SelectItem>
                    <SelectItem value="dismiss">Dismiss</SelectItem>
                    <SelectItem value="under_review">Mark Under Review</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Admin Notes (Optional)</Label>
                <Textarea
                  value={bulkAdminNotes}
                  onChange={(e) => setBulkAdminNotes(e.target.value)}
                  placeholder="Add notes about this bulk action..."
                  maxLength={1000}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">{bulkAdminNotes.length}/1000 characters</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseBulkDialog}>
                Cancel
              </Button>
              <Button
                onClick={handleBulkAction}
                disabled={bulkUpdateMutation.isPending}
              >
                {bulkUpdateMutation.isPending ? "Updating..." : "Apply Action"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
